<script setup lang="ts">
import { useToggle } from '@vueuse/core';

import DateTime, { DateTimeFormat } from '@/components/dateTime/DateTime.vue';
import RichContent from '@/components/richTextInput/RichContent.vue';

import Check from '@/icons/line/check.svg';
import Exclamation from '@/icons/line/exclamation.svg';
import Spinner from '@/icons/line/spinner.svg';

import type { ChatMessage } from '@/api/ChatApi';

const props = withDefaults(
  defineProps<{
    message: ChatMessage;
    showDateTime?: boolean;
    dateTimeFormat?: DateTimeFormat;
    isUserMessageClamped?: boolean;
  }>(),
  {
    showDateTime: false,
    dateTimeFormat: 'time',
    isUserMessageClamped: false
  }
);

const [toggleableTimestamp, toggleShowTimestamp] = useToggle(!!props.showDateTime);

const messageContent = computed(() => {
  return (
    props.message.content_blocks?.[0]?.content?.html ||
    props.message.contentBlocks?.[0]?.content?.html ||
    props.message.content ||
    props.message.content_blocks?.[0]?.content?.text ||
    props.message.contentBlocks?.[0]?.content?.text ||
    ''
  );
});

const isUserMessage = computed(() => props.message.type === 'user');

const messageStatus = computed(() => {
  if (isUserMessage.value) {
    return 'sent';
  }

  if (!props.message.id) {
    return 'pending';
  }

  return 'sent';
});

const isStreaming = computed(() => {
  return !isUserMessage.value && !messageContent.value;
});

const statusIcon = computed(() => {
  switch (messageStatus.value) {
    case 'sent':
      return Check;
    case 'pending':
      return Spinner;
    default:
      return Exclamation;
  }
});
</script>

<template>
  <div
    :class="[
      'cursor-pointer rounded-xl bg-secondary-900 px-3 pt-2.5 text-md font-medium leading-relaxed text-white md:text-sm md:leading-5',
      {
        'pb-2.5': !toggleableTimestamp,
        'pb-1.5': toggleableTimestamp,
        'animate-pulse': isStreaming
      }
    ]"
    @click="toggleShowTimestamp()"
  >
    <div>
      <RichContent
        :class="{ 'line-clamp-2': isUserMessageClamped }"
        :html="messageContent"
        :styleLinks="false"
      />
    </div>
    <div v-if="toggleableTimestamp" class="flex items-center justify-end">
      <DateTime
        v-if="message.created_at"
        :date="message.created_at"
        :format="dateTimeFormat"
        class="'mr-1 text-white' text-2xs uppercase"
      />
      <component
        :is="statusIcon"
        :class="[
          'inline-flex w-3.5 text-slate-300',
          { 'animate-spin': messageStatus === 'pending' }
        ]"
      />
    </div>
  </div>
</template>
